import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
    sendMoneyCoachMessage,
    generateMessageId,
    getConversationHistory,
    clearConversationHistory,
    getProactiveSuggestions,
    SUGGESTED_PROMPTS,
    ChatMessage,
    SpendingInsight
} from '../services/moneyCoachService';
import IconMessage2 from './Icon/IconMessage2';
import IconX from './Icon/IconX';
import IconSend from './Icon/IconSend';
import { analytics } from '../utils/analytics';

const MoneyCoachWidget = () => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [remainingQueries, setRemainingQueries] = useState<number | null>(null);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [insights, setInsights] = useState<SpendingInsight[]>([]);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Load conversation history and insights when widget opens
    useEffect(() => {
        if (isOpen && !historyLoaded) {
            // Load history
            getConversationHistory().then((history) => {
                if (history.length > 0) {
                    setMessages(history);
                }
                setHistoryLoaded(true);
            });

            // Load insights
            getProactiveSuggestions().then((suggestions) => {
                if (suggestions.length > 0) {
                    setInsights(suggestions);
                    suggestions.forEach(insight => {
                        analytics.track('insight_viewed', {
                            insight_type: 'coach_suggestion',
                            insight_title: insight.title,
                            value: undefined,
                            source: 'ai',
                        });
                    });
                }
            });
        }
    }, [isOpen, historyLoaded]);

    // Scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when opening
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    const sendMessage = useCallback(async (text: string) => {
        if (!text.trim() || isLoading) return;

        setError(null);

        // Add user message
        const userMessage: ChatMessage = {
            id: generateMessageId(),
            role: 'user',
            content: text.trim(),
            timestamp: Date.now()
        };
        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setIsLoading(true);

        // Track Amplitude event (only for the first user message in a new session)
        if (messages.length === 0) {
            analytics.track('ai_chat_started', {
                context: text.trim().substring(0, 50)
            });
        }

        try {
            const response = await sendMoneyCoachMessage(text.trim());

            // Update remaining queries from server
            if (response.remainingQueries !== undefined) {
                setRemainingQueries(response.remainingQueries);
            }

            const assistantMessage: ChatMessage = {
                id: generateMessageId(),
                role: 'assistant',
                content: response.response,
                timestamp: Date.now()
            };
            setMessages(prev => [...prev, assistantMessage]);
        } catch (err: any) {
            // Handle rate limit error specifically
            if (err?.message?.includes('limit')) {
                setRemainingQueries(0);
            }
            setError(err?.message || t('money_coach_error', { defaultValue: 'Sorry, I couldn\'t process that. Please try again.' }));
            console.error('Money Coach error:', err);
        } finally {
            setIsLoading(false);
        }
    }, [isLoading, t]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        sendMessage(input);
    };

    const handleSuggestedPrompt = (prompt: string) => {
        sendMessage(prompt);
    };

    const handleClearHistory = async () => {
        const success = await clearConversationHistory();
        if (success) {
            setMessages([]);
            setHistoryLoaded(false);
        }
    };

    return (
        <>
            {/* Floating Action Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`fixed z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 bottom-[calc(2rem_+_env(safe-area-inset-bottom))] left-[calc(2rem_+_env(safe-area-inset-left))] right-auto lg:bottom-[calc(6rem_+_env(safe-area-inset-bottom))] lg:left-auto lg:right-[calc(2rem_+_env(safe-area-inset-right))] ${isOpen
                    ? 'bg-gray-600 hover:bg-gray-700'
                    : 'bg-brand-green hover:bg-brand-green/90'
                    }`}
                aria-label={isOpen ? t('close', { defaultValue: 'Close' }) : t('money_coach', { defaultValue: 'Money Coach' })}
            >
                {isOpen ? (
                    <IconX className="w-6 h-6 text-white" />
                ) : (
                    <IconMessage2 className="w-6 h-6 text-white" />
                )}
            </button>

            {/* Chat Window */}
            {isOpen && (
                <div className="fixed z-50 w-[360px] max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100dvh-180px)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200 dark:border-gray-700 bottom-[calc(6rem_+_env(safe-area-inset-bottom))] left-[calc(1.5rem_+_env(safe-area-inset-left))] right-auto lg:bottom-[calc(10rem_+_env(safe-area-inset-bottom))] lg:left-auto lg:right-[calc(1.5rem_+_env(safe-area-inset-right))]">
                    {/* Header */}
                    <div className="bg-brand-green px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                                <span className="text-lg">🧠</span>
                            </div>
                            <div>
                                <h3 className="text-white font-semibold text-sm">
                                    {t('money_coach', { defaultValue: 'Money Coach' })}
                                </h3>
                                <p className="text-white/70 text-xs">
                                    {t('powered_by_boad_ai', { defaultValue: 'Powered by Boad AI' })}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            {messages.length > 0 && (
                                <button
                                    onClick={handleClearHistory}
                                    className="text-white/70 hover:text-white p-1 text-xs"
                                    title={t('clear_history', { defaultValue: 'Clear history' })}
                                >
                                    🗑️
                                </button>
                            )}
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white/70 hover:text-white p-1"
                            >
                                <IconX className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Messages Area */}
                    <div className="flex-1 overflow-y-auto p-4 space-y-3">
                        {messages.length === 0 ? (
                            <div className="py-4">
                                {/* Insight Cards */}
                                {insights.length > 0 && (
                                    <div className="space-y-2 mb-4">
                                        {insights.map((insight, index) => (
                                            <div
                                                key={index}
                                                className={`px-3 py-2 rounded-lg text-sm ${insight.type === 'warning'
                                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                                    : insight.type === 'success'
                                                        ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                                                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                                                    }`}
                                            >
                                                <div className="flex items-start gap-2">
                                                    <span className="text-base">{insight.icon}</span>
                                                    <div>
                                                        <p className="font-medium">{insight.title}</p>
                                                        <p className="text-xs opacity-80">{insight.message}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <p className="text-center text-gray-500 dark:text-gray-400 text-sm mb-4">
                                    {t('ask_about_finances', { defaultValue: 'Ask me anything about your finances!' })}
                                </p>
                                <p className="text-center text-gray-400 dark:text-gray-500 text-xs mb-3">
                                    {t('suggested_prompts', { defaultValue: 'Try asking:' })}
                                </p>
                                <div className="space-y-2">
                                    {SUGGESTED_PROMPTS.slice(0, 3).map((prompt, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSuggestedPrompt(prompt)}
                                            className="w-full text-left px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 transition-colors"
                                        >
                                            "{prompt}"
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${message.role === 'user'
                                            ? 'bg-brand-green text-white rounded-br-md'
                                            : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 rounded-bl-md'
                                            }`}
                                    >
                                        {message.content}
                                    </div>
                                </div>
                            ))
                        )}

                        {isLoading && (
                            <div className="flex justify-start">
                                <div className="bg-gray-100 dark:bg-gray-800 px-4 py-3 rounded-2xl rounded-bl-md">
                                    <div className="flex space-x-1">
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {error && (
                            <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-3 py-2 rounded-lg text-sm">
                                {error}
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <form onSubmit={handleSubmit} className="p-3 border-t border-gray-200 dark:border-gray-700">
                        <div className="flex gap-2">
                            <input
                                ref={inputRef}
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={t('type_your_question', { defaultValue: 'Type your question...' })}
                                className="flex-1 px-4 py-2 bg-gray-100 dark:bg-gray-800 border-none rounded-full text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-green"
                                disabled={isLoading}
                            />
                            <button
                                type="submit"
                                disabled={!input.trim() || isLoading}
                                className="w-10 h-10 rounded-full bg-brand-green text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed hover:bg-brand-green/90 transition-colors"
                            >
                                <IconSend className="w-5 h-5" />
                            </button>
                        </div>
                        {remainingQueries !== null && (
                            <p className="text-xs text-gray-400 mt-2 text-center">
                                {t('remaining_queries', { count: remainingQueries, defaultValue: `${remainingQueries} questions remaining` })}
                            </p>
                        )}
                    </form>
                </div>
            )}
        </>
    );
};

export default MoneyCoachWidget;
